import { FullScreenLoading } from "@senciamatch/frontend/components/FullScreenLoading";
import { excelParserBuildingShiftTable } from "@senciamatch/frontend/libs/excelParser/buildingShiftTables";
import { getJapaneseDayOfWeek } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { ShiftBatchInput } from "@senciamatch/shared/models/shiftBatchConverter";
import React, { type DragEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v7 as uuidv7 } from "uuid";

interface Props {
  staffShiftGroupId: string;
}

export const MatchingBuildings: React.FC<Props> = ({ staffShiftGroupId }) => {
  const navigate = useNavigate();
  const getBuildings = trpc.getBuildings.useQuery();
  const getStaffs = trpc.getStaffs.useQuery();
  const getStaffShifts = trpc.getStaffShifts.useQuery({ staffShiftGroupId });

  const buildings = getBuildings.data?.buildings ?? [];
  const staffs = getStaffs.data?.staffs ?? [];
  const staffShifts = getStaffShifts.data ?? [];

  const [parseData, setParseData] = useState<ShiftBatchInput & { errors: string[] }>();

  // 新しいshiftBatchエンドポイントを使用
  const createShiftBatchMutation = trpc.createShiftBatch.useMutation();

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file) return;
      if (!staffShiftGroupId) {
        alert("スタッフシフトグループIDが設定されていません");
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const data = e.target?.result;
        if (data instanceof ArrayBuffer) {
          const uint8Array = new Uint8Array(data);
          const parsedData = excelParserBuildingShiftTable.parse(uint8Array, buildings, staffs, staffShifts);
          console.log("parsedData", parsedData);
          setParseData(parsedData);
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [buildings, staffs, staffShifts, staffShiftGroupId],
  );

  const handleSubmit = useCallback(async () => {
    if (!parseData) return;
    if (!staffShiftGroupId) return;

    try {
      // errorsを除いたデータを送信
      const { errors, ...shiftBatchInput } = parseData;
      const result = await createShiftBatchMutation.mutateAsync(shiftBatchInput);

      alert("登録が完了しました");

      // WorkShiftGroupのIDを使用して遷移
      navigate(`/matchings/${result.workShiftGroup.id}`);
    } catch (error) {
      console.error("シフト一括作成エラー:", error);
      alert(`エラーが発生しました: ${error instanceof Error ? error.message : "不明なエラー"}`);
    }
  }, [parseData, createShiftBatchMutation, navigate, staffShiftGroupId]);

  // 月の最終日を取得
  const getLastDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  if (getBuildings.isPending || getStaffShifts.isPending) return <FullScreenLoading />;

  return (
    <div className="h-screen flex flex-col">
      <h2 className="text-2xl font-bold py-10 flex-shrink-0">物件からのシフト希望登録</h2>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="rounded-md border-dashed border-2 border-gray-200 bg-white p-4 min-h-40 flex flex-col justify-center items-center mb-4"
      >
        <p className="text-center">ファイルをここにドラッグ＆ドロップしてください</p>
      </div>

      {parseData?.errors && parseData.errors.length > 0 && (
        <div className="flex flex-col bg-red-50 p-4 mb-4">
          <p className="text-sm font-bold">{parseData.errors.length}件のエラーがあります。</p>
          {parseData.errors.map((error) => (
            <p key={uuidv7()} className="text-sm">
              {error}
            </p>
          ))}
          <p className="text-sm mt-4">
            対応してから再度登録してください。このままマッチングも可能ですが、シフトが正しく生成できない可能性があります。
          </p>
        </div>
      )}

      {parseData && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-grow overflow-hidden border bg-white border-base-100 flex flex-col">
            <div className="flex items-center justify-between">
              <p className="text-xl font-bold ml-8 mt-8">物件情報</p>
              <button
                type="button"
                className="bg-primary-700 text-white px-4 py-2 rounded-md mt-8 mx-8"
                onClick={handleSubmit}
                disabled={createShiftBatchMutation.isPending}
              >
                {createShiftBatchMutation.isPending ? "登録中..." : "一括登録実行"}
              </button>
            </div>
            <div className="flex-1 overflow-auto ml-8 mt-8 pb-8">
              <table className="w-[2450px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="sticky top-0 left-0 bg-gray-100 p-2 border border-gray-100 z-30 w-[250px]">
                      物件名
                    </th>
                    <th className="sticky top-0 left-[250px] bg-gray-100 p-2 border border-gray-100 z-20 w-[100px]">
                      時間
                    </th>
                    {Array.from(
                      { length: getLastDayOfMonth(parseData.workShiftGroup.year, parseData.workShiftGroup.month) },
                      (_, i) => {
                        const day = i + 1;
                        const dayOfWeek = getJapaneseDayOfWeek(
                          parseData.workShiftGroup.year,
                          parseData.workShiftGroup.month,
                          day,
                        );
                        const bgColor =
                          dayOfWeek === "日" ? "bg-red-100" : dayOfWeek === "土" ? "bg-blue-100" : "bg-gray-100";
                        return (
                          <th
                            key={`day-${day}`}
                            className={`sticky top-0 ${bgColor} p-2 border border-gray-100 z-10 w-[50px]`}
                          >
                            {day}
                            <br />
                            {dayOfWeek}
                          </th>
                        );
                      },
                    )}
                  </tr>
                </thead>
                <tbody>
                  {parseData.buildingShiftRows.map((row) => {
                    const timeText = `${String(row.startHour).padStart(2, "0")}:${String(row.startMinute).padStart(2, "0")}-${String(row.endHour).padStart(2, "0")}:${String(row.endMinute).padStart(2, "0")}`;

                    // この物件に関連するworkShiftsを取得
                    const relatedWorkShifts = parseData.workShifts.filter((ws) => ws.buildingShiftRowId === row.id);
                    const staffIds = [...new Set(relatedWorkShifts.map((ws) => ws.staffId))];

                    return (
                      <React.Fragment key={uuidv7()}>
                        {/* 物件行 */}
                        <tr>
                          <td className="sticky left-0 p-2 bg-white border border-gray-100 z-10 font-bold0">
                            {row.name}
                          </td>
                          <td className="sticky left-[250px] p-2 bg-white border border-gray-100 text-center font-mono z-10">
                            {timeText}
                          </td>
                          {Array.from(
                            {
                              length: getLastDayOfMonth(parseData.workShiftGroup.year, parseData.workShiftGroup.month),
                            },
                            (_, dayIndex) => {
                              const day = dayIndex + 1;
                              const buildingShift = parseData.buildingShifts.find(
                                (bs) => bs.buildingShiftRowId === row.id && bs.day === day,
                              );

                              return (
                                <td
                                  key={uuidv7()}
                                  className="border border-gray-100 w-[50px] h-[30px] bg-white text-center text-xs"
                                >
                                  {buildingShift && <>{buildingShift.staffCount}</>}
                                </td>
                              );
                            },
                          )}
                        </tr>

                        {/* スタッフ行 */}
                        {staffIds.map((staffId) => {
                          if (!staffId) return null;
                          const staff = staffs.find((s) => s.id === staffId);
                          const staffName = staff?.name || `スタッフID: ${staffId}`;

                          return (
                            <tr key={uuidv7()}>
                              <td className="sticky left-0 p-2 bg-white border border-gray-100 z-10 pl-8 text-gray-600" />

                              <td className="sticky left-[250px] p-2 bg-white border border-gray-100 z-10">
                                {staffName}
                              </td>
                              {Array.from(
                                {
                                  length: getLastDayOfMonth(
                                    parseData.workShiftGroup.year,
                                    parseData.workShiftGroup.month,
                                  ),
                                },
                                (_, dayIndex) => {
                                  const day = dayIndex + 1;
                                  const hasWorkShift = relatedWorkShifts.some(
                                    (ws) => ws.staffId === staffId && ws.day === day,
                                  );
                                  return (
                                    <td
                                      key={`staff-${row.buildingId}-${row.startHour}-${row.startMinute}-${row.endHour}-${row.endMinute}-${staffId}-day-${day}`}
                                      className="border border-gray-100 w-[50px] h-[30px] bg-white text-center"
                                    >
                                      {hasWorkShift && <span className="text-blue-600">○</span>}
                                    </td>
                                  );
                                },
                              )}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
